import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Pill, Download, Thermometer, ShieldCheck, ArrowLeft, FileText,
  Snowflake, AlertTriangle, CheckCircle2, BookOpen, Sun, Building2, Mail,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public-facing Cold-Chain Shipping & Temperature Stability page.
 *
 * Content is fully admin-editable via `Admin → Pharmacy Partners →
 * Shipping Study Page`. Per-pharmacy stability PDFs are uploaded through
 * `Admin → Pharmacies → [pharmacy] → Supporting documents` with the
 * "This is the shipping stability study" checkbox ticked.
 */

/** Render an admin-edited prose block.
 *  - Paragraphs are separated by blank lines
 *  - A block where every non-empty line starts with "- " renders as <ul>
 */
function ProseBlock({ text }) {
  if (!text) return null;
  const blocks = text.split(/\n\s*\n/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        if (lines.length === 0) return null;
        const allBullets = lines.every((l) => l.startsWith("- ") || l.startsWith("• "));
        if (allBullets) {
          return (
            <ul key={bi}>
              {lines.map((l, i) => (
                <li key={i}>{l.replace(/^[-•]\s*/, "")}</li>
              ))}
            </ul>
          );
        }
        return <p key={bi}>{block.trim()}</p>;
      })}
    </>
  );
}

export default function ShippingStudyPage() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/shipping-study-content`)
      .then((r) => setC(r.data))
      .catch(() => setC({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="shipping-study-loading">
        Loading…
      </div>
    );
  }

  const pharmStudies = c?.pharmacy_studies || [];
  const greenFlags = c?.green_flags || [];
  const redFlags = c?.red_flags || [];
  const sources = c?.sources || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shipping-study-page">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Link to="/terms-of-service" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to Terms
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-widest text-purple-600 font-semibold" data-testid="ssp-eyebrow">
              {c?.eyebrow}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="ssp-heading">
              {c?.heading}
            </h1>
            {c?.subheading && (
              <p className="text-sm text-slate-500 mt-1" data-testid="ssp-subheading">{c.subheading}</p>
            )}
          </div>
          <a href={`${API}/api/shipping-study.pdf`} target="_blank" rel="noopener noreferrer" data-testid="ssp-download-pdf">
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-sm whitespace-nowrap">
              <FileDown className="w-4 h-4 mr-2" /> Download as PDF
            </Button>
          </a>
        </div>

        {/* Hero card */}
        <div
          className="rounded-2xl bg-gradient-to-br from-emerald-50 via-emerald-50 to-amber-50 border border-emerald-200 p-6 sm:p-7 mb-8 shadow-sm"
          data-testid="ssp-hero"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              {c?.hero_eyebrow && (
                <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">
                  {c.hero_eyebrow}
                </p>
              )}
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mt-1 leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {c?.hero_title}
              </h2>
              <p className="text-slate-700 mt-2 text-sm leading-relaxed">{c?.hero_body}</p>
            </div>
          </div>
        </div>

        {/* Quick-stat squares */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
            <ShieldCheck className="w-5 h-5 text-emerald-600 mb-2" />
            <p className="text-2xl font-black text-emerald-700">7 days</p>
            <p className="text-[11px] uppercase tracking-wider text-emerald-700">Stable in transit</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <Thermometer className="w-5 h-5 text-amber-600 mb-2" />
            <p className="text-2xl font-black text-amber-700">≤ 104 °F</p>
            <p className="text-[11px] uppercase tracking-wider text-amber-700">Peak transit temp tested</p>
          </div>
          <div className="rounded-xl bg-purple-50 border border-purple-200 p-4">
            <FileText className="w-5 h-5 text-purple-600 mb-2" />
            <p className="text-2xl font-black text-purple-700">USP &lt;797&gt;</p>
            <p className="text-[11px] uppercase tracking-wider text-purple-700">Sterile compounding standard</p>
          </div>
        </div>

        {/* Editable prose body */}
        <div className="bg-white rounded-2xl border border-purple-100 p-6 sm:p-8 prose prose-slate max-w-none" data-testid="ssp-prose">
          <ProseBlock text={c?.prose} />
        </div>

        {/* Green / Red flag side-by-side */}
        {(greenFlags.length > 0 || redFlags.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {greenFlags.length > 0 && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="ssp-green-flags">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  <p className="font-semibold text-emerald-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {c?.green_flags_title}
                  </p>
                </div>
                <ul className="space-y-2 text-sm text-emerald-900">
                  {greenFlags.map((g, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {redFlags.length > 0 && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5" data-testid="ssp-red-flags">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-rose-700" />
                  <p className="font-semibold text-rose-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {c?.red_flags_title}
                  </p>
                </div>
                <ul className="space-y-2 text-sm text-rose-900">
                  {redFlags.map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                {c?.red_flags_footer && (
                  <p className="text-xs text-rose-700 mt-3">{c.red_flags_footer}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-pharmacy study downloads */}
        {pharmStudies.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl border border-purple-100 p-6 sm:p-8" data-testid="ssp-pharmacy-studies">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Building2 className="w-5 h-5 text-purple-600" />
              {c?.pharmacy_studies_title}
            </h2>
            {c?.pharmacy_studies_intro && (
              <p className="text-sm text-slate-600 mb-4">{c.pharmacy_studies_intro}</p>
            )}
            <div className="space-y-2">
              {pharmStudies.map((p) => (
                <div
                  key={p.pharmacy_id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 hover:border-purple-300 transition-colors p-4"
                  data-testid={`ssp-pharm-${p.pharmacy_slug || p.pharmacy_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.pharmacy_name}</p>
                    {p.has_study ? (
                      <p className="text-xs text-slate-500 truncate">
                        {p.filename} · uploaded {new Date(p.uploaded_at).toLocaleDateString()}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400">
                        {c?.pharmacy_missing_label || "Study available on request"}
                      </p>
                    )}
                  </div>
                  {p.has_study ? (
                    <a
                      href={`${API}${p.download_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`ssp-pharm-download-${p.pharmacy_slug || p.pharmacy_id}`}
                    >
                      <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white">
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </a>
                  ) : (
                    <a
                      href="mailto:customer.service@glpflo.com?subject=Shipping%20stability%20study%20request"
                      data-testid={`ssp-pharm-email-${p.pharmacy_slug || p.pharmacy_id}`}
                    >
                      <Button size="sm" variant="outline" className="rounded-full">
                        <Mail className="w-4 h-4 mr-1" /> Email us
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sources */}
        {sources.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl border border-purple-100 p-6 sm:p-8" data-testid="ssp-sources">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <BookOpen className="w-5 h-5 text-purple-600" />
              {c?.sources_title}
            </h2>
            {c?.sources_subtitle && (
              <p className="text-sm text-slate-600 mb-4">{c.sources_subtitle}</p>
            )}
            <div className="space-y-3">
              {sources.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/40 transition-colors p-4"
                  data-testid={`ssp-source-${i}`}
                >
                  <p className="text-sm font-semibold text-purple-700">{s.label}</p>
                  {s.detail && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.detail}</p>}
                  {s.href && <p className="text-[11px] text-slate-400 mt-1 truncate">{s.href}</p>}
                </a>
              ))}
            </div>
            {c?.sources_footer && (
              <p className="text-xs text-slate-500 mt-4">{c.sources_footer}</p>
            )}
          </div>
        )}

        {/* Reminder strips */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <div className="rounded-2xl bg-purple-50 border border-purple-200 p-4 flex items-center gap-3" data-testid="ssp-reminder-refrigerate">
            <Snowflake className="w-6 h-6 text-purple-600 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wider text-purple-700 font-semibold">Refrigerate Immediately Upon Delivery</p>
              <p className="text-sm text-slate-700">Store at 36–46°F (2–8°C) as soon as the package is received and keep refrigerated to maintain the Beyond-Use Date (BUD) printed on your pharmacy label.</p>
            </div>
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3" data-testid="ssp-reminder-never-freeze">
            <Sun className="w-6 h-6 text-amber-600 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Never freeze the vial</p>
              <p className="text-sm text-slate-700">Freezing is the one excursion that can damage peptide medications. Discard and contact us if it arrives frozen solid.</p>
            </div>
          </div>
        </div>

        {c?.contact_footer && (
          <p className="text-sm text-slate-500 mt-6" data-testid="ssp-contact-footer">{c.contact_footer}</p>
        )}
      </div>
    </div>
  );
}
